import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SubscriptionForm from '../components/SubscriptionForm';
import Modal from '../components/Modal';
import CookieBanner from '../components/CookieBanner';
import { getArticleById } from '../newsData';

const Article = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const [isThankYouModalOpen, setIsThankYouModalOpen] = useState(false);

  useEffect(() => {
    const foundArticle = getArticleById(id);
    if (!foundArticle) {
      navigate('/');
      return;
    }
    setArticle(foundArticle);
  }, [id, navigate]);

  const handleSubscribeClick = () => {
    setIsSubscribeModalOpen(true);
  };

  const handleSubscriptionSuccess = () => {
    setIsSubscribeModalOpen(false);
    setIsThankYouModalOpen(true);
  };

  const closeThankYouModal = () => {
    setIsThankYouModalOpen(false);
  };

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Split content into paragraphs for better formatting
  const paragraphs = article.content
    ? article.content.split('\n\n').filter(p => p.trim())
    : [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header onSubscribeClick={handleSubscribeClick} />
      
      <main className="flex-grow bg-neutral-50">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
          {/* Article Container with white background */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            {/* Header Section */}
            <div className="px-6 md:px-12 pt-8 md:pt-12 pb-6 border-b border-neutral-200">
              {/* Category */}
              {article.category && article.category.length > 0 && (
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-primary-600 text-white text-xs font-bold uppercase tracking-wider">
                    {article.category[0]}
                  </span>
                </div>
              )}

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-6 leading-tight font-serif">
                {article.title}
              </h1>

              {/* Meta Information - News Style */}
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm text-neutral-600 border-t border-neutral-200 pt-4">
                {article.creator && article.creator.length > 0 && (
                  <div>
                    <span className="font-semibold text-neutral-900">By </span>
                    <span className="font-medium">{article.creator[0]}</span>
                  </div>
                )}
                <div>
                  <span>{formatDate(article.pubDate)}</span>
                </div>
                {article.source_name && (
                  <div>
                    <span className="text-neutral-500">Source: </span>
                    <span className="font-medium">{article.source_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Main Image with Caption */}
            {article.image_url && (
              <figure className="my-8 px-6 md:px-12">
                <div className="overflow-hidden">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-auto"
                  />
                </div>
                {article.description && (
                  <figcaption className="mt-3 text-sm text-neutral-500 italic border-l-4 border-primary-600 pl-3">
                    {article.description}
                  </figcaption>
                )}
              </figure>
            )}

            {/* Article Content - News Style */}
            <div className="px-6 md:px-12 pb-8 md:pb-12">
              <div className="max-w-3xl mx-auto">
                {paragraphs.map((paragraph, index) => {
                  // First paragraph with drop cap
                  if (index === 0) {
                    const firstChar = paragraph.charAt(0);
                    const restOfParagraph = paragraph.slice(1);
                    return (
                      <p
                        key={index}
                        className="text-neutral-800 leading-relaxed mb-6 text-lg md:text-xl font-serif relative"
                      >
                        <span className="float-left text-7xl md:text-8xl font-bold text-primary-600 leading-none mr-2 pt-2 font-serif">
                          {firstChar}
                        </span>
                        <span className="block">{restOfParagraph}</span>
                      </p>
                    );
                  }
                  
                  // Regular paragraphs
                  return (
                    <p
                      key={index}
                      className="text-neutral-800 leading-relaxed mb-6 text-lg md:text-xl font-serif"
                    >
                      {paragraph}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mid-Article CTA - News Style */}
          <div className="my-12 bg-neutral-100 border-l-4 border-primary-600 p-6 md:p-8">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-xl md:text-2xl font-bold text-neutral-900 mb-3 font-serif">
                Stay Informed
              </h3>
              <p className="text-neutral-700 mb-6 text-lg">
                Subscribe to our newsletter to receive the latest news and updates directly in your inbox.
              </p>
              <SubscriptionForm onSubmit={handleSubscriptionSuccess} />
            </div>
          </div>

          {/* Article Footer */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden mt-8">
            <div className="px-6 md:px-12 py-6 border-t border-neutral-200">
              {/* Keywords */}
              {article.keywords && article.keywords.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">
                    Topics
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {article.keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-neutral-100 text-neutral-700 text-sm font-medium hover:bg-neutral-200 transition-colors"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Article Meta Footer */}
              <div className="pt-6 border-t border-neutral-200">
                <div className="flex flex-wrap gap-6 text-sm text-neutral-600">
                  <div>
                    <span className="font-semibold text-neutral-900">Published: </span>
                    <span>{formatDate(article.pubDate)}</span>
                  </div>
                  {article.source_name && (
                    <div>
                      <span className="font-semibold text-neutral-900">Source: </span>
                      <span>{article.source_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA - News Style */}
          <div className="mt-12 bg-primary-600 text-white p-8 md:p-10 rounded-lg">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 font-serif">
                Don't Miss Out on Important News
              </h3>
              <p className="mb-6 text-primary-100 text-lg">
                Subscribe now and get the latest updates delivered to your inbox.
              </p>
              <SubscriptionForm onSubmit={handleSubscriptionSuccess} />
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* Subscribe Modal */}
      <Modal
        isOpen={isSubscribeModalOpen}
        onClose={() => setIsSubscribeModalOpen(false)}
        title="Subscribe to Our Newsletter"
      >
        <SubscriptionForm onSubmit={handleSubscriptionSuccess} />
      </Modal>

      {/* Thank You Modal */}
      <Modal
        isOpen={isThankYouModalOpen}
        onClose={closeThankYouModal}
        title="Thank You for Subscribing!"
      >
        <p className="mb-6">
          Now you will receive the most up-to-date news.
        </p>
        <button
          onClick={closeThankYouModal}
          className="btn-primary w-full"
        >
          OK
        </button>
      </Modal>

      <CookieBanner />
    </div>
  );
};

export default Article;
